import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import expensesDashboardService from '../../services/expensesDashboardService'
import { burnExpenseService } from '../../services/burnExpenseService'
import { formatCurrency as formatCurrencyAmount } from '../../utils/currencyFormat'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  CreditCard,
  Receipt,
  Wallet,
  Banknote,
  UserCheck,
  Flame,
  RefreshCw,
  HelpCircle
} from 'lucide-react'
// Consolidated CSS is imported globally'

const getCurrentMonth = () => {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`
}

const Expenses = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [showHelp, setShowHelp] = useState(false)
  const [helpTab, setHelpTab] = useState('masters') // 'masters' | 'transactions' | 'reports'
  const [dashboardData, setDashboardData] = useState(null)
  const [burnSummary, setBurnSummary] = useState(null)
  const [burnMonth, setBurnMonth] = useState(getCurrentMonth())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  useEffect(() => {
    if (moduleView === 'overview') {
      loadBurnSummary()
    }
  }, [moduleView, burnMonth])

  const loadBurnSummary = async () => {
    try {
      const res = await burnExpenseService.getBurnSummary({ month: burnMonth })
      if (res?.success) setBurnSummary(res.data)
    } catch (e) {
      console.error('Burn summary:', e)
    }
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await expensesDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else {
        if (!response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading expenses dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => formatCurrencyAmount(amount, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  const mastersHelpContent = (
    <>
      <h3>Expense Masters – Overview</h3>
      <p>Masters define the foundational data used across expense claims, approvals, reimbursements, and burn tracking. Configure these before processing transactions.</p>

      <h4>Employees</h4>
      <p><strong>Purpose:</strong> Maintain employee profiles linked to expenses and set per-employee expense limits or policies.</p>
      <p><strong>Details:</strong> Use the Employees card to open the employee master list. From there you can add, edit, or view employees. Each record typically includes name, department, designation, and optional expense limits. This data is used when employees submit expense claims and when assigning approvers.</p>

      <h4>Expense Categories</h4>
      <p><strong>Purpose:</strong> Define expense types and classification (e.g. Travel, Meals, Office, Conveyance) for consistent reporting and policy rules.</p>
      <p><strong>Details:</strong> Open Expense Categories to create and manage category codes and names. Categories appear in expense claim forms and in reports. You can mark categories as billable, set default limits, or link them to burn groups if you use burn tracking.</p>

      <h4>Payment Methods</h4>
      <p><strong>Purpose:</strong> Configure payment options and reimbursement methods (e.g. Bank Transfer, Cash, UPI, Company Card).</p>
      <p><strong>Details:</strong> The Payment Methods master lists all allowed payment types. When processing reimbursements or advance payments, users choose from these methods. Add or edit methods to match your organisation’s payment channels.</p>

      <h4>Burn Category Master</h4>
      <p><strong>Purpose:</strong> Define categories for burn/cost tracking such as Salary, Infra, Office, and other groupings used in burn reports and dashboards.</p>
      <p><strong>Details:</strong> Burn categories group expenses for high-level burn analysis (e.g. monthly burn by Salary, Infra, Office). Create and maintain category names and optionally map expense categories or accounts to these burn groups. Used by Burn Expense Entry and Burn Reports.</p>

      <h4>Approvers</h4>
      <p><strong>Purpose:</strong> Set up the expense approval hierarchy and approval limits (who can approve up to what amount or type).</p>
      <p><strong>Details:</strong> The Approvers master defines approver roles, users, and their approval limits. Expense claims are routed based on this setup. Configure levels (e.g. L1, L2) and limits so that claims are sent to the right approvers automatically.</p>

      <h3>How to use</h3>
      <p>Click any card to open that master’s list screen. Use <strong>New</strong> or <strong>Create</strong> (or equivalent) to add records. Edit or view existing rows from the list. Ensure Employees, Expense Categories, Payment Methods, and Approvers are set up before using Expense Claims and Approvals; configure Burn Category Master if you use burn tracking and Burn Expense Entry.</p>
    </>
  )

  const transactionsHelpContent = (
    <>
      <h3>Expense Transactions – Overview</h3>
      <p>Transactions cover the day-to-day flow of expense submission, approval, reimbursement, advances, burn entry, and financial-year budgeting. Use these after masters are configured.</p>

      <h4>Expense Claims</h4>
      <p><strong>Purpose:</strong> Employees submit expense reports with line items, receipts, and supporting details for reimbursement or charge-back.</p>
      <p><strong>Details:</strong> Open Expense Claims to view and create claims. Each claim typically includes employee, dates, expense category, amount, currency, and optional receipt upload. Claims move to Approvals once submitted. Use the list to filter by status (Draft, Submitted, Approved, Rejected) and the create form to add new claims or attach receipts.</p>

      <h4>Approvals</h4>
      <p><strong>Purpose:</strong> Approvers review, approve, or reject submitted expense claims according to policy and limits.</p>
      <p><strong>Details:</strong> The Approvals screen shows claims pending your action. You can approve, reject (with reason), or request more information. Approved claims typically flow to Reimbursements for payment. Use filters to see pending, approved, or rejected items and complete the approval workflow.</p>

      <h4>Reimbursements</h4>
      <p><strong>Purpose:</strong> Process approved expenses for payment and track reimbursement status.</p>
      <p><strong>Details:</strong> Reimbursements lists approved claims ready for payment. Record payment method, reference number, and date. Status (e.g. Pending, Processed, Paid) helps track completion. This is where finance completes the payout and links it to bank or payroll if needed.</p>

      <h4>Advance Payments</h4>
      <p><strong>Purpose:</strong> Manage advance payments to employees and their settlement against expense claims.</p>
      <p><strong>Details:</strong> Use Advance Payments to create and track advances (e.g. travel advance). When the employee submits claims later, you can settle them against the advance. The screen helps track outstanding advances and settlement status.</p>

      <h4>Burn Expense Entry</h4>
      <p><strong>Purpose:</strong> Record expenses into burn categories (Salary, Infra, Office, etc.) for burn tracking and reporting.</p>
      <p><strong>Details:</strong> Enter or import expenses and assign them to a burn category and period (e.g. month). Data feeds Burn Reports and the Overview burn summary. Ensure Burn Category Master is set up first. Use this for non-claim expenses (e.g. payroll, infra) or to align claim categories with burn groups.</p>

      <h4>Financial Year Budget</h4>
      <p><strong>Purpose:</strong> Define and manage Indian financial year (April–March) budgets by category or department and track approvals.</p>
      <p><strong>Details:</strong> The Financial Year Budget screen provides a budget grid for the FY. Set budget amounts by period or category, submit for approval, and compare with actuals in Budget Reports or Budget vs Actual. Use it for annual planning and spend control.</p>

      <h3>How to use</h3>
      <p>Click any card to open that transaction’s list or main screen. For claims: create a new claim, add line items and receipts, then submit. Approvers use Approvals to approve or reject. Finance uses Reimbursements to mark payments and Advance Payments to manage advances. Use Burn Expense Entry for burn data and Financial Year Budget for FY budget setup and approvals.</p>
    </>
  )

  const getHelpTitle = () => {
    if (helpTab === 'masters') return 'Expense Masters – Help'
    if (helpTab === 'transactions') return 'Expense Transactions – Help'
    if (helpTab === 'reports') return 'Expense Reports – Help'
    return 'Expenses – Help'
  }

  const getHelpContent = () => {
    if (helpTab === 'masters') return mastersHelpContent
    if (helpTab === 'transactions') return transactionsHelpContent
    return mastersHelpContent
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Expenses</h1>
          <p style={{textAlign: 'left', margin: 0}}>Track and manage business expenses and reimbursements</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/expenses')} />
          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Expense Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.monthlyExpenses?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.monthlyExpenses?.change >= 0 ? '+' : ''}{dashboardData?.monthlyExpenses?.change?.toFixed(1) || 0}%</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : formatCurrency(dashboardData?.monthlyExpenses?.amount || 0, dashboardData?.monthlyExpenses?.currency || 'INR')}
              </div>
              <div className="metric-title">Monthly Expenses</div>
              <div className="metric-subtitle">vs last month</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <Receipt size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.expenseClaims?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.expenseClaims?.change >= 0 ? '+' : ''}{dashboardData?.expenseClaims?.change || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.expenseClaims?.count || 0)}
              </div>
              <div className="metric-title">Expense Claims</div>
              <div className="metric-subtitle">This month</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className="metric-trend negative">
                  <TrendingDown size={12} />
                  <span>{dashboardData?.pendingApproval?.status || 'AWAITING'}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.pendingApproval?.count || 0)}
              </div>
              <div className="metric-title">Pending Approval</div>
              <div className="metric-subtitle">Claims</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <CreditCard size={16} />
                </div>
                <div className="metric-trend neutral">
                  <CheckCircle size={12} />
                  <span>{dashboardData?.reimbursementRate?.status || 'ON TIME'}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : `${dashboardData?.reimbursementRate?.rate || 0}%`}
              </div>
              <div className="metric-title">Reimbursement Rate</div>
              <div className="metric-subtitle">Processing efficiency</div>
            </div>
          </div>

          {/* Burn Tracking Summary */}
          <div className="section-header d-flex justify-content-between align-items-center" style={{ marginTop: '24px', marginBottom: '12px' }}>
            <h3 className="mb-0">Burn Tracking</h3>
            <input type="month" className="form-control" style={{ width: 'auto' }} value={burnMonth} onChange={(e) => setBurnMonth(e.target.value)} />
          </div>
          <div className="dashboard-metrics-grid">
            <div className="metric-card revenue">
              <div className="metric-header"><div className="metric-icon"><Flame size={16} /></div></div>
              <div className="metric-value">{loading ? '...' : formatCurrency(burnSummary?.totalBurn || 0, 'INR')}</div>
              <div className="metric-title">Total Burn</div>
              <div className="metric-subtitle">Current month</div>
            </div>
            <div className="metric-card customers">
              <div className="metric-header"><div className="metric-icon"><DollarSign size={16} /></div></div>
              <div className="metric-value">{loading ? '...' : formatCurrency(burnSummary?.groupTotals?.Salary || 0, 'INR')}</div>
              <div className="metric-title">Salary Burn</div>
              <div className="metric-subtitle">Salary group</div>
            </div>
            <div className="metric-card churn">
              <div className="metric-header"><div className="metric-icon"><Briefcase size={16} /></div></div>
              <div className="metric-value">{loading ? '...' : formatCurrency(burnSummary?.groupTotals?.Infra || 0, 'INR')}</div>
              <div className="metric-title">Infra Burn</div>
              <div className="metric-subtitle">Infrastructure</div>
            </div>
            <div className="metric-card burn">
              <div className="metric-header"><div className="metric-icon"><Receipt size={16} /></div></div>
              <div className="metric-value">{loading ? '...' : formatCurrency(burnSummary?.groupTotals?.Office || 0, 'INR')}</div>
              <div className="metric-title">Office Burn</div>
              <div className="metric-subtitle">Office expenses</div>
            </div>
            <div className="metric-card burn">
              <div className="metric-header"><div className="metric-icon"><RefreshCw size={16} /></div></div>
              <div className="metric-value">{loading ? '...' : formatCurrency(burnSummary?.recurringTotal || 0, 'INR')}</div>
              <div className="metric-title">Recurring Burn</div>
              <div className="metric-subtitle">Recurring expenses</div>
            </div>
          </div>

          {/* Burn Charts */}
          {(burnSummary?.groupTotals && Object.keys(burnSummary.groupTotals).length > 0) || (burnSummary?.dailyBurn && burnSummary.dailyBurn.length > 0) ? (
            <div className="dashboard-charts-section" style={{ marginTop: '20px' }}>
              {burnSummary?.groupTotals && Object.keys(burnSummary.groupTotals).length > 0 && (
                <div className="dashboard-chart-card revenue-chart">
                  <div className="chart-header"><h3>Burn by Category</h3><p>{burnMonth}</p></div>
                  <div className="chart-content" style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={Object.entries(burnSummary.groupTotals || {}).map(([k, v]) => ({ name: k, amount: v }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(v) => `₹${v / 1000}k`} />
                        <Tooltip formatter={(v) => [formatCurrency(v, 'INR'), 'Amount']} />
                        <Bar dataKey="amount" fill="#0d6efd" name="Amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              {burnSummary?.dailyBurn && burnSummary.dailyBurn.length > 0 && (
                <div className="dashboard-chart-card customer-health">
                  <div className="chart-header"><h3>Daily Burn Trend</h3><p>{burnMonth}</p></div>
                  <div className="chart-content" style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={burnSummary.dailyBurn.map(d => ({ ...d, date: d.date }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis tickFormatter={(v) => `₹${v / 1000}k`} />
                        <Tooltip formatter={(v) => [formatCurrency(v, 'INR'), 'Amount']} />
                        <Line type="monotone" dataKey="amount" stroke="#198754" strokeWidth={2} name="Daily Burn" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Expense Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length || 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems && dashboardData.actionItems.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const iconMap = {
                      approval: AlertTriangle,
                      compliance: Receipt,
                      category: Wallet
                    }
                    const Icon = iconMap[item.type] || AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon">
                          <Icon size={16} />
                        </div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Expense Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length || 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity && dashboardData.teamActivity.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Expense Trend</h3>
                <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart
                  series={dashboardData?.expenseTrend}
                  valueKey="revenue"
                  formatTooltip={(v) => formatCurrencyAmount(v, 'INR')}
                  emptyMessage="No expense data for this financial year"
                />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Expense Category Distribution</h3>
                <p>Current month</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</div>
                  ) : dashboardData?.categoryDistribution?.length > 0 ? (
                    dashboardData.categoryDistribution.map((row, index) => {
                      const classes = ['excellent', 'good', 'at-risk', 'churned']
                      const cls = classes[index % classes.length]
                      return (
                        <div key={row.name || index} className={`health-segment ${cls}`}>
                          <div className="segment-bar" style={{ width: `${Math.max(row.percent || 0, row.amount > 0 ? 4 : 0)}%` }} />
                          <div className="segment-info">
                            <span className="segment-label">{row.name}</span>
                            <span className="segment-count">{formatCurrency(row.amount || 0, 'INR')}</span>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                      No expense claims this month
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="expenses-masters-content">
            <div className="section-header d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: '16px' }}>
              <h3 className="mb-0">Expense Masters</h3>
              <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => { setHelpTab('masters'); setShowHelp(true); }} title="Help">
                <HelpCircle size={18} />
                <span className="d-none d-sm-inline">Help</span>
              </button>
            </div>
            <div className="expenses-masters-grid">
              <div className="expenses-master-card clickable" onClick={() => navigate('/expenses/employee-master')}>
                <div className="expenses-master-icon">
                  <Users size={24} />
                </div>
                <div className="expenses-master-content">
                  <h3>Employees</h3>
                  <p>Employee profiles and expense limits</p>
                  <div className="expenses-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.employees ?? 0} Employees`}
                  </div>
                </div>
              </div>
              <div className="expenses-master-card clickable" onClick={() => navigate('/expenses/expense-category-master')}>
                <div className="expenses-master-icon">
                  <Target size={24} />
                </div>
                <div className="expenses-master-content">
                  <h3>Expense Categories</h3>
                  <p>Expense types and classification</p>
                  <div className="expenses-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.categories ?? 0} Categories`}
                  </div>
                </div>
              </div>
              <div className="expenses-master-card clickable" onClick={() => navigate('/expenses/payment-method-master')}>
                <div className="expenses-master-icon">
                  <CreditCard size={24} />
                </div>
                <div className="expenses-master-content">
                  <h3>Payment Methods</h3>
                  <p>Payment options and reimbursement methods</p>
                  <div className="expenses-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.paymentMethods ?? 0} Methods`}
                  </div>
                </div>
              </div>
              <div className="expenses-master-card clickable" onClick={() => navigate('/expenses/approver-master')}>
                <div className="expenses-master-icon">
                  <UserCheck size={24} />
                </div>
                <div className="expenses-master-content">
                  <h3>Approvers</h3>
                  <p>Expense approval hierarchy and limits</p>
                  <div className="expenses-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.approvers ?? 0} Approvers`}
                  </div>
                </div>
              </div>
              <div className="expenses-master-card clickable" onClick={() => navigate('/expenses/burn-category-master')}>
                <div className="expenses-master-icon">
                  <Flame size={24} />
                </div>
                <div className="expenses-master-content">
                  <h3>Burn Category Master</h3>
                  <p>Categories for burn tracking (Salary, Infra, Office, etc.)</p>
                  <div className="expenses-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.burnCategories ?? 0} Categories`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="expenses-transactions-content">
            <div className="section-header d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: '16px' }}>
              <h3 className="mb-0">Expense Transactions</h3>
              <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => { setHelpTab('transactions'); setShowHelp(true); }} title="Help">
                <HelpCircle size={18} />
                <span className="d-none d-sm-inline">Help</span>
              </button>
            </div>
            <div className="expenses-transactions-grid">
              <ModuleOverviewCard
                prefix="expenses-transaction"
                moduleName="Expenses"
                showArrow={false}
                onClick={() => navigate('/expenses?view=overview')}
              />
              <div className="expenses-transaction-card clickable" onClick={() => navigate('/expenses/expense-claim')}>
                <div className="expenses-transaction-icon">
                  <Receipt size={24} />
                </div>
                <div className="expenses-transaction-content">
                  <h3>Expense Claims</h3>
                  <p>Employee expense submissions and receipts</p>
                  <div className="expenses-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.claims ?? 0} Claims`}
                  </div>
                </div>
              </div>
              <div className="expenses-transaction-card clickable" onClick={() => navigate('/expenses/expense-approval')}>
                <div className="expenses-transaction-icon">
                  <UserCheck size={24} />
                </div>
                <div className="expenses-transaction-content">
                  <h3>Approvals</h3>
                  <p>Expense approval and rejection workflows</p>
                  <div className="expenses-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.approvals ?? 0} Approvals`}
                  </div>
                </div>
              </div>
              <div className="expenses-transaction-card clickable" onClick={() => navigate('/expenses/expense-reimbursement')}>
                <div className="expenses-transaction-icon">
                  <Banknote size={24} />
                </div>
                <div className="expenses-transaction-content">
                  <h3>Reimbursements</h3>
                  <p>Payment processing and reimbursement tracking</p>
                  <div className="expenses-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.reimbursements ?? 0} Reimbursements`}
                  </div>
                </div>
              </div>
              <div className="expenses-transaction-card clickable" onClick={() => navigate('/expenses/advance-payment')}>
                <div className="expenses-transaction-icon">
                  <Wallet size={24} />
                </div>
                <div className="expenses-transaction-content">
                  <h3>Advance Payments</h3>
                  <p>Advance payments and settlement tracking</p>
                  <div className="expenses-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.advances ?? 0} Advances`}
                  </div>
                </div>
              </div>
              <div className="expenses-transaction-card clickable" onClick={() => navigate('/expenses/burn-entry')}>
                <div className="expenses-transaction-icon">
                  <Flame size={24} />
                </div>
                <div className="expenses-transaction-content">
                  <h3>Burn Expense Entry</h3>
                  <p>Record expenses for burn tracking</p>
                  <div className="expenses-transaction-count">Entries</div>
                </div>
              </div>
              <div className="expenses-transaction-card clickable" onClick={() => navigate('/expenses/financial-year-budget')}>
                <div className="expenses-transaction-icon">
                  <Calendar size={24} />
                </div>
                <div className="expenses-transaction-content">
                  <h3>Financial Year Budget</h3>
                  <p>Indian FY (April–March) budget grid and approvals</p>
                  <div className="expenses-transaction-count">FY Budget</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="expenses-reports-content">
            <div className="expenses-reports-grid">
              <div className="expenses-report-card clickable" onClick={() => navigate('/expenses/expense-analysis')}>
                <div className="expenses-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="expenses-report-content">
                  <h3>Expense Analysis</h3>
                  <p>Expense trends and category analysis</p>
                  <div className="expenses-report-status">Updated Today</div>
                </div>
              </div>
              <div className="expenses-report-card clickable" onClick={() => navigate('/expenses/employee-reports')}>
                <div className="expenses-report-icon">
                  <Users size={24} />
                </div>
                <div className="expenses-report-content">
                  <h3>Employee Reports</h3>
                  <p>Individual employee expense tracking</p>
                  <div className="expenses-report-status">Updated Today</div>
                </div>
              </div>
              <div className="expenses-report-card clickable" onClick={() => navigate('/expenses/department-reports')}>
                <div className="expenses-report-icon">
                  <Briefcase size={24} />
                </div>
                <div className="expenses-report-content">
                  <h3>Department Reports</h3>
                  <p>Department-wise expense allocation</p>
                  <div className="expenses-report-status">Updated Today</div>
                </div>
              </div>
              <div className="expenses-report-card clickable" onClick={() => navigate('/expenses/budget-reports')}>
                <div className="expenses-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="expenses-report-content">
                  <h3>Budget Reports</h3>
                  <p>Budget vs actual expense analysis</p>
                  <div className="expenses-report-status">Updated Today</div>
                </div>
              </div>
              <div className="expenses-report-card clickable" onClick={() => navigate('/expenses/budget-vs-actual')}>
                <div className="expenses-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="expenses-report-content">
                  <h3>Budget vs Actual (Burn)</h3>
                  <p>Indian FY burn budget vs actual by month</p>
                  <div className="expenses-report-status">FY Budget</div>
                </div>
              </div>
              <div className="expenses-report-card clickable" onClick={() => navigate('/expenses/burn-reports')}>
                <div className="expenses-report-icon">
                  <Flame size={24} />
                </div>
                <div className="expenses-report-content">
                  <h3>Burn Reports</h3>
                  <p>Burn trend and category breakdown</p>
                  <div className="expenses-report-status">Updated Today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={getHelpTitle()}>
        {getHelpContent()}
      </HelpModal>
    </div>
  )
}

export default Expenses




